import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Alert } from './Alert';
import { EmptyState } from './EmptyState';
import { Pagination, type PaginationProps } from './Pagination';
import { SkeletonTable } from './Skeleton';

export interface Column<T> {
  /** Identificador único da coluna. */
  id: string;
  header: ReactNode;
  cell: (row: T, index: number) => ReactNode;
  className?: string;
  headerClassName?: string;
  /** Alinhamento numérico à direita. */
  align?: 'left' | 'right' | 'center';
  /** Oculta a coluna em telas pequenas. */
  hideOnMobile?: boolean;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string;
  loading?: boolean;
  error?: unknown;
  errorMessage?: string;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  emptyAction?: ReactNode;
  onRowClick?: (row: T) => void;
  pagination?: Omit<PaginationProps, 'className'>;
  caption?: string;
  className?: string;
  /** Linha extra ao final (totais, por exemplo). */
  footer?: ReactNode;
}

const ALIGN = { left: 'text-left', right: 'text-right', center: 'text-center' } as const;

export function Table<T>({
  columns,
  rows,
  rowKey,
  loading,
  error,
  errorMessage,
  emptyTitle = 'Nenhum registro encontrado',
  emptyDescription,
  emptyAction,
  onRowClick,
  pagination,
  caption,
  className,
  footer,
}: TableProps<T>) {
  if (loading) {
    return (
      <div className={cn('surface-card overflow-hidden', className)}>
        <SkeletonTable columns={Math.min(columns.length, 5)} />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('surface-card p-4', className)}>
        <Alert tone="danger" title="Não foi possível carregar os dados">
          {errorMessage ?? 'Tente novamente em instantes.'}
        </Alert>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className={cn('surface-card overflow-hidden', className)}>
        <EmptyState title={emptyTitle} description={emptyDescription} action={emptyAction} />
      </div>
    );
  }

  return (
    <div className={cn('surface-card overflow-hidden', className)}>
      <div className="table-scroll">
        <table className="w-full min-w-[640px] border-collapse text-sm">
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead>
            <tr className="border-b border-line bg-elevated/60">
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  className={cn(
                    'whitespace-nowrap px-4 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted',
                    ALIGN[column.align ?? 'left'],
                    column.hideOnMobile && 'hidden md:table-cell',
                    column.headerClassName,
                  )}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={rowKey(row, index)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                tabIndex={onRowClick ? 0 : undefined}
                onKeyDown={
                  onRowClick
                    ? (event) => {
                        if (event.key === 'Enter') onRowClick(row);
                      }
                    : undefined
                }
                className={cn(
                  'border-b border-line/70 last:border-0',
                  onRowClick && 'cursor-pointer transition hover:bg-elevated focus:bg-elevated',
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.id}
                    className={cn(
                      'px-4 py-3 align-middle text-fg',
                      ALIGN[column.align ?? 'left'],
                      column.hideOnMobile && 'hidden md:table-cell',
                      column.className,
                    )}
                  >
                    {column.cell(row, index)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
          {footer && <tfoot className="border-t border-line bg-elevated/60">{footer}</tfoot>}
        </table>
      </div>
      {pagination && pagination.total > 0 && <Pagination {...pagination} />}
    </div>
  );
}
